import { validate } from 'email-validator'

export const MUTATION = {
  createOrUpdateDemographicData: 'createOrUpdateDemographicData',
  updatePatient: 'updatePatient',
  updatePatientContactInfo: 'updatePatientContactInfo',
}

export const ERROR_MESSAGES = {
  invalidEmail: 'Invalid email',
  invalidPhone: 'Invalid phone number. Be sure to include the country code.',
  visitDate: 'Only one of Clinical Visit Date or Next Visit Date can be filled',
  wholeNumber: 'Enter a whole number',
}

export const FORM_FIELD_WIDTH = {
  full: {
    name: 'full',
    value: 1,
  },
  half: {
    name: 'half',
    value: 0.5,
  },
}

export const FORM_FIELD_WIDTH_UNITS = {
  numeric: 'numeric',
  percentage: 'percentage',
}

export const FORM_FIELDS_METADATA = {
  clinicalSite: {
    key: 'clinicalSites',
    label: 'Clinical Site',
    layout: FORM_FIELD_WIDTH.half.name,
    mutation: MUTATION.createOrUpdateDemographicData,
    name: 'identifiableInfo.demographicData.clinicalSite',
    type: 'select',
  },
  clinicalVisitDate: {
    errorMessage: ERROR_MESSAGES.visitDate,
    key: 'clinicalVisitDate',
    label: 'Clinical Visit',
    layout: FORM_FIELD_WIDTH.half.name,
    mutation: MUTATION.createOrUpdateDemographicData,
    name: 'identifiableInfo.demographicData.clinicalVisit',
    type: 'date',
    validate: ({
      formData,
      getNestedObjectValue,
      patient,
      value: clinicalVisitDate,
    }) => {
      const nextVisitMonthYearPath =
        'identifiableInfo.demographicData.nextVisitMonthYear'

      const nextVisitMonthYearPreviouslyExisted = getNestedObjectValue({
        object: patient,
        path: nextVisitMonthYearPath,
      })

      const nextVisitMonthYearUpdatedInFormData = getNestedObjectValue({
        object: formData,
        path: nextVisitMonthYearPath,
      })

      return clinicalVisitDate &&
        (nextVisitMonthYearUpdatedInFormData ||
          (nextVisitMonthYearPreviouslyExisted &&
            nextVisitMonthYearUpdatedInFormData === undefined)) // eslint-disable-line no-undefined
        ? ERROR_MESSAGES.visitDate
        : null
    },
  },
  dateOfBirth: {
    key: 'dateOfBirth',
    label: 'Date of Birth',
    layout: FORM_FIELD_WIDTH.half.name,
    mutation: MUTATION.createOrUpdateDemographicData,
    name: 'identifiableInfo.demographicData.dateOfBirth',
    type: 'date',
  },
  ethnicity: {
    key: 'ethnicity',
    label: 'Ethnicity',
    layout: FORM_FIELD_WIDTH.half.name,
    mutation: MUTATION.createOrUpdateDemographicData,
    name: 'identifiableInfo.demographicData.ethnicity',
    type: 'select',
  },
  facilityName: {
    key: 'facilityName',
    label: 'Facility Name',
    layout: FORM_FIELD_WIDTH.half.name,
    mutation: MUTATION.createOrUpdateDemographicData,
    name: 'identifiableInfo.demographicData.facilityName',
    type: 'text',
  },
  gender: {
    key: 'gender',
    label: 'Gender',
    layout: FORM_FIELD_WIDTH.half.name,
    mutation: MUTATION.createOrUpdateDemographicData,
    name: 'identifiableInfo.demographicData.gender',
    type: 'select',
  },
  geneticMarkers: {
    key: 'geneticMarkers',
    label: 'Genetic Markers',
    layout: FORM_FIELD_WIDTH.full.name,
    mutation: MUTATION.createOrUpdateDemographicData,
    name: 'identifiableInfo.demographicData.geneticMarkers',
    type: 'multiSelect',
  },
  height: {
    errorMessage: ERROR_MESSAGES.wholeNumber,
    key: 'height',
    label: 'Height (in)',
    layout: FORM_FIELD_WIDTH.half.name,
    mutation: MUTATION.createOrUpdateDemographicData,
    name: 'identifiableInfo.demographicData.height',
    type: 'text',
    validate: ({ isNumericString, value: height }) =>
      isNumericString(height) || !height ? null : ERROR_MESSAGES.wholeNumber,
  },
  initialProvider: {
    name: 'initialProvider',
  },
  nextVisitMonthYear: {
    datePickerProps: {
      dateFormat: 'YYYY-MM',
      views: ['year', 'month'],
    },
    errorMessage: ERROR_MESSAGES.visitDate,
    key: 'nextVisitMonthYear',
    label: 'Next Visit Month',
    layout: FORM_FIELD_WIDTH.half.name,
    mutation: MUTATION.createOrUpdateDemographicData,
    name: 'identifiableInfo.demographicData.nextVisitMonthYear',
    type: 'date',
    validate: ({
      formData,
      getNestedObjectValue,
      patient,
      value: nextVisitMonthYear,
    }) => {
      const clinicalVisitPath = 'identifiableInfo.demographicData.clinicalVisit'

      const clinicalVisitDatePreviouslyExisted = getNestedObjectValue({
        object: patient,
        path: clinicalVisitPath,
      })

      const clinicalVisitDateUpdatedInFormData = getNestedObjectValue({
        object: formData,
        path: clinicalVisitPath,
      })

      return nextVisitMonthYear &&
        (clinicalVisitDateUpdatedInFormData ||
          (clinicalVisitDatePreviouslyExisted &&
            clinicalVisitDateUpdatedInFormData === undefined)) // eslint-disable-line no-undefined
        ? ERROR_MESSAGES.visitDate
        : null
    },
  },
  otherConditions: {
    key: 'diagnoses',
    label: 'Other Conditions',
    layout: FORM_FIELD_WIDTH.full.name,
    mutation: MUTATION.createOrUpdateDemographicData,
    name: 'identifiableInfo.demographicData.otherConditions',
    type: 'multiSelect',
  },
  patientCodeName: {
    key: 'patientCodeName',
    label: 'Code Name',
    layout: FORM_FIELD_WIDTH.full.name,
    mutation: MUTATION.updatePatient,
    name: 'codeName',
    type: 'text',
  },
  patientContactEmail: {
    errorMessage: ERROR_MESSAGES.invalidEmail,
    key: 'patientContactEmail',
    label: 'Contact Email',
    layout: FORM_FIELD_WIDTH.full.name,
    name: 'identifiableInfo.contactEmail',
    mutation: MUTATION.updatePatientContactInfo,
    type: 'email',
    validate: ({ value: email }) =>
      validate(email) || email === 'undefined' || !email
        ? null
        : ERROR_MESSAGES.invalidEmail,
  },
  patientFirstName: {
    key: 'firstName',
    label: 'First Name',
    layout: FORM_FIELD_WIDTH.half.name,
    mutation: MUTATION.updatePatientContactInfo,
    name: 'identifiableInfo.firstName',
    type: 'text',
  },
  patientLastName: {
    key: 'lastName',
    label: 'Last Name',
    layout: FORM_FIELD_WIDTH.half.name,
    mutation: MUTATION.updatePatientContactInfo,
    name: 'identifiableInfo.lastName',
    type: 'text',
  },
  patientPhoneNumber: {
    errorMessage: ERROR_MESSAGES.invalidPhone,
    key: 'patientPhoneNumber',
    label: 'Phone Number',
    layout: FORM_FIELD_WIDTH.half.name,
    mutation: MUTATION.updatePatientContactInfo,
    name: 'identifiableInfo.phoneNumber',
    type: 'phone',
    validate: ({ value: phoneNumber, validatePhoneNumber }) =>
      validatePhoneNumber({
        errorMessage: ERROR_MESSAGES.invalidPhone,
        phoneNumber,
      }),
  },
  patientPhoneNumberAlternate: {
    errorMessage: ERROR_MESSAGES.invalidPhone,
    key: 'patientPhoneNumberAlternate',
    label: 'Alternate Phone Number',
    layout: FORM_FIELD_WIDTH.half.name,
    mutation: MUTATION.updatePatientContactInfo,
    name: 'identifiableInfo.alternatePhoneNumber',
    type: 'phone',
    validate: ({ value: phoneNumber, validatePhoneNumber }) =>
      validatePhoneNumber({
        errorMessage: ERROR_MESSAGES.invalidPhone,
        phoneNumber,
      }),
  },
  pdDiagnosis: {
    key: 'pdDiagnosis',
    label: 'PD Diagnosis',
    layout: FORM_FIELD_WIDTH.half.name,
    mutation: MUTATION.createOrUpdateDemographicData,
    name: 'identifiableInfo.demographicData.pdDiagnosis',
    type: 'select',
  },
  pdDiagnosisDate: {
    key: 'pdDiagnosisDate',
    label: 'PD Diagnosis Date',
    layout: FORM_FIELD_WIDTH.half.name,
    mutation: MUTATION.createOrUpdateDemographicData,
    name: 'identifiableInfo.demographicData.pdDiagnosisDate',
    type: 'date',
  },
  providerAddressLine1: {
    name: 'identifiableInfo.demographicData.providerAddress.addressLine1',
  },
  providerAddressLine2: {
    name: 'identifiableInfo.demographicData.providerAddress.addressLine2',
  },
  providerCity: {
    name: 'identifiableInfo.demographicData.providerAddress.city',
  },
  providerNpi: {
    name: 'identifiableInfo.demographicData.providerNpi',
  },
  providerState: {
    name: 'identifiableInfo.demographicData.providerAddress.state',
  },
  providerZipCode: {
    name: 'identifiableInfo.demographicData.providerAddress.zipCode',
  },
  providerFirstName: {
    key: 'providerFirstName',
    label: 'Provider First Name',
    layout: FORM_FIELD_WIDTH.half.name,
    optionValueIdentifier: 'first_name',
    name: 'identifiableInfo.demographicData.providerInfo.firstName',
    type: 'autoComplete',
  },
  providerLastName: {
    key: 'providerLastName',
    label: 'Provider Last Name',
    layout: FORM_FIELD_WIDTH.half.name,
    name: 'identifiableInfo.demographicData.providerInfo.lastName',
    optionValueIdentifier: 'last_name',
    type: 'autoComplete',
  },
  race: {
    key: 'race',
    label: 'Race',
    layout: FORM_FIELD_WIDTH.half.name,
    mutation: MUTATION.createOrUpdateDemographicData,
    name: 'identifiableInfo.demographicData.race',
    type: 'select',
  },
  stateForSearch: {
    key: 'stateForSearch',
    label: 'State',
    layout: FORM_FIELD_WIDTH.full.name,
    mutation: MUTATION.createOrUpdateDemographicData,
    name: 'identifiableInfo.demographicData.state',
    type: 'select',
  },
  visitType: {
    key: 'visitType',
    label: 'Appointment Type',
    layout: FORM_FIELD_WIDTH.full.name,
    mutation: MUTATION.createOrUpdateDemographicData,
    name: 'identifiableInfo.demographicData.visitType',
    type: 'select',
  },
  weight: {
    errorMessage: ERROR_MESSAGES.wholeNumber,
    key: 'weight',
    label: 'Weight (lbs)',
    layout: FORM_FIELD_WIDTH.half.name,
    mutation: MUTATION.createOrUpdateDemographicData,
    name: 'identifiableInfo.demographicData.weight',
    type: 'text',
    validate: ({ isNumericString, value: weight }) =>
      isNumericString(weight) || !weight ? null : ERROR_MESSAGES.wholeNumber,
  },
}

export const FORM_FIELDS = {
  clinicalInfoFields: [
    FORM_FIELDS_METADATA.clinicalVisitDate,
    FORM_FIELDS_METADATA.nextVisitMonthYear,
    FORM_FIELDS_METADATA.visitType,
  ],
  codeName: [FORM_FIELDS_METADATA.patientCodeName],
  demographicInfo: [
    FORM_FIELDS_METADATA.dateOfBirth,
    FORM_FIELDS_METADATA.gender,
    FORM_FIELDS_METADATA.height,
    FORM_FIELDS_METADATA.weight,
    FORM_FIELDS_METADATA.race,
    FORM_FIELDS_METADATA.ethnicity,
  ],
  facilityInfo: [
    FORM_FIELDS_METADATA.facilityName,
    FORM_FIELDS_METADATA.clinicalSite,
  ],
  healthInfo: [
    FORM_FIELDS_METADATA.pdDiagnosis,
    FORM_FIELDS_METADATA.pdDiagnosisDate,
    FORM_FIELDS_METADATA.geneticMarkers,
    FORM_FIELDS_METADATA.otherConditions,
  ],
  personalInfo: [
    FORM_FIELDS_METADATA.patientFirstName,
    FORM_FIELDS_METADATA.patientLastName,
    FORM_FIELDS_METADATA.patientPhoneNumber,
    FORM_FIELDS_METADATA.patientPhoneNumberAlternate,
    FORM_FIELDS_METADATA.patientContactEmail,
  ],
  providerName: [
    FORM_FIELDS_METADATA.providerFirstName,
    FORM_FIELDS_METADATA.providerLastName,
  ],
}

export const PROVIDER_SEARCH_FIELDS = {
  providerFirstName: FORM_FIELDS_METADATA.providerFirstName.name,
  providerLastName: FORM_FIELDS_METADATA.providerLastName.name,
}

export const TYPOGRAPHY = {
  instructions: 'body14',
  readOnlyInfoHeading: 'body16B',
  readOnlyInfoLine: 'body16',
  sectionHeading: 'head22',
  sectionSubHeading: 'head18',
}

// This is the mapping used by StrivePD
export const US_STATES_AND_ABBREVIATIONS = {
  Alabama: 'AL',
  Alaska: 'AK',
  'American Samoa': 'AS',
  Arizona: 'AZ',
  Arkansas: 'AR',
  California: 'CA',
  Colorado: 'CO',
  Connecticut: 'CT',
  Delaware: 'DE',
  'District of Columbia': 'DC',
  Florida: 'FL',
  Georgia: 'GA',
  Guam: 'GU',
  Hawaii: 'HI',
  Idaho: 'ID',
  Illinois: 'IL',
  Indiana: 'IN',
  Iowa: 'IA',
  Kansas: 'KS',
  Kentucky: 'KY',
  Louisiana: 'LA',
  Maine: 'ME',
  Maryland: 'MD',
  Massachusetts: 'MA',
  Michigan: 'MI',
  Minnesota: 'MN',
  Mississippi: 'MS',
  Missouri: 'MO',
  Montana: 'MT',
  Nebraska: 'NE',
  Nevada: 'NV',
  'New Hampshire': 'NH',
  'New Jersey': 'NJ',
  'New Mexico': 'NM',
  'New York': 'NY',
  'North Carolina': 'NC',
  'North Dakota': 'ND',
  'Northern Mariana Islands': 'MP',
  Ohio: 'OH',
  Oklahoma: 'OK',
  Oregon: 'OR',
  Pennsylvania: 'PA',
  Palau: 'PW',
  'Puerto Rico': 'PR',
  'Rhode Island': 'RI',
  'South Carolina': 'SC',
  'South Dakota': 'SD',
  Tennessee: 'TN',
  Texas: 'TX',
  Utah: 'UT',
  Vermont: 'VT',
  Virginia: 'VA',
  'Virgin Islands': 'VI',
  Washington: 'WA',
  'West Virginia': 'WV',
  Wisconsin: 'WI',
  Wyoming: 'WY',
}
